import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  MapPin,
  Star,
  Phone,
  Globe,
  Clock,
  Plus,
  Edit,
  Trash2,
  Users,
  ChevronDown,
  ChevronUp,
  Monitor,
  Sparkles,
  LayoutGrid
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { PageContainer } from '@/components/layout/PageContainer';
import { AppBreadcrumbs } from '@/components/layout/AppBreadcrumbs';
import { venueService } from '@/lib/api/venue.service';
import { venueRoomService } from '@/lib/api/venue-room.service';
import { VenueRoomForm } from '@/components/VenueRoomForm';
import { AutoDetectRoomsModal } from '@/components/AutoDetectRoomsModal';
import type { VenueWithGoogleDetails } from '@/types/venue.types';
import type { VenueRoomWithConfigs, CreateVenueRoomDto } from '@/types/venue-room.types';
import { ROOM_TYPE_LABELS, SEATING_CONFIG_LABELS } from '@/types/venue-room.types';

export const VenueDetail: React.FC = () => {
  const { t } = useTranslation(['venues', 'common']);
  const { id } = useParams();
  const venueId = id ? parseInt(id) : 0;

  const [venue, setVenue] = useState<VenueWithGoogleDetails | null>(null);
  const [rooms, setRooms] = useState<VenueRoomWithConfigs[]>([]);
  const [loading, setLoading] = useState(true);
  const [roomsLoading, setRoomsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Room form state
  const [showRoomForm, setShowRoomForm] = useState(false);
  const [editingRoom, setEditingRoom] = useState<VenueRoomWithConfigs | null>(null);
  const [formLoading, setFormLoading] = useState(false);

  // Collapsible sections
  const [showDetails, setShowDetails] = useState(true);
  const [showRooms, setShowRooms] = useState(true);

  // Auto-detect modal
  const [showAutoDetect, setShowAutoDetect] = useState(false);

  useEffect(() => {
    if (venueId) {
      loadVenue();
      loadRooms();
    }
  }, [venueId]);

  const loadVenue = async () => {
    try {
      setLoading(true);
      const data = await venueService.getVenueWithDetails(venueId);
      setVenue(data);
      setError(null);
    } catch (err) {
      setError(t('venues:errors.loadVenueDetails'));
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const loadRooms = async () => {
    try {
      setRoomsLoading(true);
      const data = await venueRoomService.getRooms(venueId);
      setRooms(data);
    } catch (err) {
      console.error('Failed to load rooms:', err);
    } finally {
      setRoomsLoading(false);
    }
  };

  const handleCreateRoom = async (data: CreateVenueRoomDto) => {
    try {
      setFormLoading(true);
      await venueRoomService.createRoom(venueId, data);
      await loadRooms();
      setShowRoomForm(false);
    } catch (err: any) {
      const msg = err?.response?.data?.error || t('venues:errors.createRoom');
      alert(msg);
    } finally {
      setFormLoading(false);
    }
  };

  const handleUpdateRoom = async (data: CreateVenueRoomDto) => {
    if (!editingRoom) return;
    try {
      setFormLoading(true);
      await venueRoomService.updateRoom(venueId, editingRoom.id, data);
      await loadRooms();
      setEditingRoom(null);
      setShowRoomForm(false);
    } catch (err: any) {
      const msg = err?.response?.data?.error || t('venues:errors.updateRoom');
      alert(msg);
    } finally {
      setFormLoading(false);
    }
  };

  const handleDeleteRoom = async (roomId: number, roomName: string) => {
    if (!confirm(t('venues:dialogs.deleteRoomConfirm', { roomName }))) return;
    try {
      await venueRoomService.deleteRoom(venueId, roomId);
      setRooms(prev => prev.filter(r => r.id !== roomId));
    } catch (err: any) {
      const msg = err?.response?.data?.error || t('venues:errors.deleteRoom');
      alert(msg);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex items-center gap-2 text-muted-foreground">
          <Sparkles className="h-5 w-5 animate-pulse" />
          <span>{t('venues:status.loadingVenue')}</span>
        </div>
      </div>
    );
  }

  if (error || !venue) {
    return (
      <PageContainer width="default" className="space-y-4">
        <AppBreadcrumbs />
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md">
          {error || t('venues:errors.venueNotFound')}
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer width="default" className="space-y-6" data-testid="venue-detail-page">
      <AppBreadcrumbs />
      {/* Header */}
      <div className="flex-1">
        <h1 className="text-2xl font-bold text-gray-900" data-testid="venue-detail-name">{venue.name}</h1>
        {venue.formatted_address && (
          <p className="text-sm text-gray-600 flex items-center gap-1 mt-1">
            <MapPin className="h-3 w-3" />
            {venue.formatted_address}
          </p>
        )}
      </div>

      {/* Venue Details - Collapsible */}
      <div className="border rounded-lg">
        <button
          onClick={() => setShowDetails(!showDetails)}
          className="flex items-center justify-between w-full p-4 text-left hover:bg-gray-50"
        >
          <h2 className="text-lg font-semibold">{t('venues:sections.venueDetails')}</h2>
          {showDetails ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        </button>

        {showDetails && (
          <div className="px-4 pb-4 space-y-3 border-t pt-3">
            {venue.rating && (
              <div className="flex items-center gap-2">
                <Star className="h-4 w-4 text-yellow-400" />
                <span className="text-sm font-medium">{venue.rating}</span>
                {venue.user_ratings_total && (
                  <span className="text-sm text-gray-500">({venue.user_ratings_total} reviews)</span>
                )}
              </div>
            )}

            {venue.formatted_phone_number && (
              <div className="flex items-center gap-2">
                <Phone className="h-4 w-4 text-gray-400" />
                <span className="text-sm">{venue.formatted_phone_number}</span>
              </div>
            )}

            {venue.website && (
              <div className="flex items-center gap-2">
                <Globe className="h-4 w-4 text-gray-400" />
                <a href={venue.website} target="_blank" rel="noopener noreferrer"
                  className="text-sm text-blue-600 hover:text-blue-800">
                  {t('venues:actions.visitWebsite')}
                </a>
              </div>
            )}

            {venue.opening_hours?.weekday_text && (
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <Clock className="h-4 w-4 text-gray-400" />
                  <span className="text-sm font-medium">{t('venues:labels.hours')}</span>
                </div>
                <div className="text-xs text-gray-600 space-y-0.5 ml-6">
                  {venue.opening_hours.weekday_text.map((hour, i) => (
                    <div key={i}>{hour}</div>
                  ))}
                </div>
              </div>
            )}

            {venue.types && venue.types.length > 0 && (
              <div className="flex flex-wrap gap-1">
                {venue.types
                  .filter(t => !['point_of_interest', 'establishment'].includes(t))
                  .map(type => (
                    <Badge key={type} variant="secondary" className="text-xs">
                      {type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                    </Badge>
                  ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Rooms & Spaces - Collapsible */}
      <div className="border rounded-lg" data-testid="venue-rooms-section">
        <button
          onClick={() => setShowRooms(!showRooms)}
          className="flex items-center justify-between w-full p-4 text-left hover:bg-gray-50"
        >
          <div className="flex items-center gap-2">
            <h2 className="text-lg font-semibold">{t('venues:sections.roomsSpaces')}</h2>
            <Badge variant="secondary" className="text-xs">{rooms.length}</Badge>
          </div>
          {showRooms ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        </button>

        {showRooms && (
          <div className="px-4 pb-4 border-t pt-3 space-y-4">
            {/* Add Room Button */}
            {!showRoomForm && !editingRoom && (
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => { setShowRoomForm(true); setEditingRoom(null); }}
                  data-testid="add-room-btn"
                >
                  <Plus className="h-3 w-3 mr-1" />
                  {t('venues:actions.addRoomSpace')}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowAutoDetect(true)}
                  className="text-purple-700 border-purple-300 hover:bg-purple-50"
                  data-testid="auto-detect-rooms-btn"
                >
                  <Sparkles className="h-3 w-3 mr-1" />
                  {t('venues:actions.autoDetectFromWebsite')}
                </Button>
              </div>
            )}

            {/* Room Form */}
            {(showRoomForm || editingRoom) && (
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base">
                    {editingRoom ? t('venues:titles.editRoom', { name: editingRoom.name }) : t('venues:titles.newRoomSpace')}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <VenueRoomForm
                    room={editingRoom}
                    onSubmit={editingRoom ? handleUpdateRoom : handleCreateRoom}
                    onCancel={() => { setShowRoomForm(false); setEditingRoom(null); }}
                    loading={formLoading}
                  />
                </CardContent>
              </Card>
            )}

            {/* Room List */}
            {roomsLoading ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
              </div>
            ) : rooms.length > 0 ? (
              <div className="space-y-3">
                {rooms.map(room => (
                  <Card key={room.id} data-testid={`room-card-${room.id}`}>
                    <CardContent className="pt-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <h3 className="font-semibold text-base" data-testid={`room-name-${room.id}`}>
                              {room.name}
                            </h3>
                            <Badge variant="outline" className="text-xs">
                              {ROOM_TYPE_LABELS[room.room_type] || room.room_type}
                            </Badge>
                            {room.seating_configs.length === 0 && (
                              <Badge variant="destructive" className="text-[10px]">
                                {t('venues:status.incomplete')}
                              </Badge>
                            )}
                          </div>

                          {room.description && (
                            <p className="text-sm text-gray-600 mt-1">{room.description}</p>
                          )}

                          {/* Quick Info */}
                          <div className="flex flex-wrap items-center gap-3 mt-2 text-xs text-gray-500">
                            {room.max_capacity && (
                              <span className="flex items-center gap-1">
                                <Users className="h-3 w-3" />
                                Up to {room.max_capacity}
                              </span>
                            )}
                            {room.floor_area_sqft && (
                              <span>{room.floor_area_sqft} sq ft</span>
                            )}
                            {room.has_av_equipment && (
                              <span className="flex items-center gap-1">
                                <Monitor className="h-3 w-3" />
                                {t('venues:labels.av')}
                              </span>
                            )}
                            {room.has_stage && <span>{t('venues:labels.stage')}</span>}
                            {room.has_dance_floor && <span>{t('venues:labels.danceFloor')}</span>}
                            {room.is_accessible && <span>{t('venues:labels.accessible')}</span>}
                          </div>

                          {/* Seating Configs */}
                          {room.seating_configs.length > 0 ? (
                            <div className="mt-2">
                              <div className="flex items-center gap-1 mb-1">
                                <LayoutGrid className="h-3 w-3 text-gray-400" />
                                <span className="text-xs text-gray-500 font-medium">{t('venues:labels.seatingConfigurations')}</span>
                              </div>
                              <div className="flex flex-wrap gap-1">
                                {room.seating_configs.map(sc => (
                                  <Badge
                                    key={sc.config_type}
                                    variant={sc.is_default ? 'default' : 'secondary'}
                                    className="text-[10px]"
                                  >
                                    {SEATING_CONFIG_LABELS[sc.config_type]}
                                    {sc.capacity ? ` (${sc.capacity})` : ''}
                                  </Badge>
                                ))}
                              </div>
                            </div>
                          ) : (
                            <div className="mt-2 flex items-start gap-2 px-3 py-2 bg-amber-50 border border-amber-200 rounded text-xs text-amber-700">
                              <span className="text-sm">⚠️</span>
                              <div>
                                <p className="font-medium">{t('venues:empty.noSeatingLayouts')}</p>
                                <p className="text-[11px] mt-0.5">{t('venues:empty.noSeatingLayoutsHelp')}</p>
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Actions */}
                        <div className="flex items-center gap-1 ml-3">
                          <Button
                            variant="ghost"
                            size="icon-sm"
                            onClick={() => {
                              setEditingRoom(room);
                              setShowRoomForm(true);
                            }}
                            title={t('common:actions.edit')}
                            data-testid={`edit-room-${room.id}`}
                          >
                            <Edit className="h-3.5 w-3.5" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon-sm"
                            onClick={() => handleDeleteRoom(room.id, room.name)}
                            className="text-red-500 hover:text-red-700 hover:bg-red-50"
                            title={t('common:actions.delete')}
                            data-testid={`delete-room-${room.id}`}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              !showRoomForm && (
                <div className="text-center py-8 text-gray-500" data-testid="no-rooms-message">
                  <LayoutGrid className="h-8 w-8 mx-auto mb-2 text-gray-300" />
                  <p className="text-sm">{t('venues:empty.noRooms')}</p>
                  <p className="text-xs mt-1">{t('venues:empty.noRoomsHelp')}</p>
                </div>
              )
            )}
          </div>
        )}
      </div>
      {/* Auto-Detect Rooms Modal */}
      {showAutoDetect && (
        <AutoDetectRoomsModal
          venueId={venueId}
          venueWebsite={venue.website}
          venueName={venue.name}
          onComplete={() => {
            setShowAutoDetect(false);
            loadRooms();
          }}
          onClose={() => setShowAutoDetect(false)}
        />
      )}
    </PageContainer>
  );
};
